<template>
  <div>
    <breadcrumbs :home="product.name + ': Reduce inventory'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-row>
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh"/>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col>
              <in-bond v-model="transaction.bonded" @change="changeBond"/>
            </v-col>
          </v-row>

          <cases-bottles :cases.sync="transaction.cases" :bottles.sync="transaction.bottles" :caseSize="product.caseSize" :availableCount="available" avaiableSuffix="Available"/>

          <v-row>
            <v-col cols="6" sm="3" lg="2">
              <decimal-number-input label="Total value to reduce" v-model="value" :disabled="calculateValue"/>
            </v-col>
            <v-col cols="6" sm="3" lg="2">
              <v-checkbox label="Calculate value" v-model="calculateValue" dense class="mt-1"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" sm="3" lg="2"  v-if="company.settings.lotTrackingForProducts && (transaction.bonded || company.settings.lotTrackingForDutyPaid)">
              <v-combobox v-model="transaction.lot" outlined dense label="Lot" :items="currentLots" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
            </v-col>
            <v-col cols="12" sm="6" lg="4" v-if="sites.length>1">
              <v-autocomplete outlined dense label="Site" v-model="transaction.siteId" :items="sites" item-value="id" item-text="name"/>
            </v-col>
          </v-row>

          <v-row v-if="values && company.connectedToAccounting">
            <v-col cols="12" sm="6" lg="4">
              <account-picker v-model="transaction.expenseAccountRef" label="Inventory adjustment account"
                              :accounts="accounts"
                              hint="An account to balance the inventory asset account. Usually an inventory shrinkage expense or cost of goods account."/>
            </v-col>
          </v-row>

          <v-row v-if="transaction.bonded">
            <v-col>
              <v-radio-group v-model="transaction.excise" class="mt-0">
                <v-radio value="ProductReductionBreakage" label="Breakage"/>
                <v-radio value="ProductReductionForOtherPurpose" label="Non-duty-paid spirits removed for other purposes"/>
                <v-radio value="ProductReductionDutyPaidOtherPurposes" label="Duty-paid spirits removed for other purposes"/>
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.required,fieldRules.maxLength(255)]" counter="255"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="transactionId && company.permissions.includes('inventory.write')">
          <v-switch v-if="!transaction.reconciliation && (transaction.date > company.excisePeriodEnd || edit)" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else-if="!transaction.reconciliation" class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
          <div v-else class="warning--text">Reduction was done as part of an inventory reconciliation and cannot be edited.</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import InBond from '@/components/InBond'
import CasesBottles from '@/components/CasesBottles'

export default {
  name: 'InventoryProductsReduceInventory',
  mixins: [confirmLeave],
  components: {InBond,CasesBottles},
	props: {
    productId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      init: true,
      edit: false,
      product: {name:'',sites:[]},
      valid:true,
      saving:false,
      accounts: [],
      calculateValue: true,
      value: 0,
      transaction: {productId:this.productId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), excise:'ProductReductionBreakage', bonded:null,
                    cases:0, bottles: 0, value:null, lot:'', comments:'Breakage',expenseAccountRef:'',siteId:''},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.transactionId, this.transaction.bonded ? {B262:true} : undefined);
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    },
    currentLots() {
      return this.product.sites.filter(site => site.siteId === this.transaction.siteId &&
                                              (this.transaction.bonded && site.inBondBottles || !this.transaction.bonded && site.dutyPaidBottles))
                                .map(site => site.lot);
    },
    available() {
      let site;
      if (!this.company.settings.lotTrackingForProducts ||
          !this.company.settings.lotTrackingForDutyPaid && !this.transaction.bonded)
        site = this.product.sites.reduce((site, productSite) => {
          if (productSite.siteId === this.transaction.siteId) {
            site.dutyPaidBottles += productSite.dutyPaidBottles;
            site.inBondBottles += productSite.inBondBottles;
          }
          return site;
        }, {dutyPaidBottles:0, inBondBottles:0});
      else
        site = this.product.sites.find(site => site.siteId === this.transaction.siteId && site.lot === this.transaction.lot);
      return (this.transaction.bonded ? site?.inBondBottles : site?.dutyPaidBottles) || 0;
    }
  },
  watch: {
    calculateValue() {
      if (this.calculateValue)
        this.transaction.value = null;
    },
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    const results = await this.$apiCall(`query InventoryProductsReduceInventory($currency:String!,$id:ID!,$edit:Boolean!) {
                                              accounts(currency:$currency) {
                                                ref,parentRef,name,type,typeName
                                              }
                                              productReduceInventory(id:$id) @include(if:$edit) {
                                                id,productId,date,bonded,cases,bottles,lot,value,excise,comments,expenseAccountRef,siteId,reconciliation
                                              }
                                              companyAccounts @skip(if:$edit) {inventoryAdjustment}
                                            }`,
                                        {currency:this.company.settings.homeCurrency,
                                         id:this.transactionId||'',edit:this.transactionId!=undefined});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.accounts = results.data.accounts;
    if (this.transactionId) {
      this.transaction = results.data.productReduceInventory;
      this.value = this.transaction.value || 0;
      this.calculateValue = this.transaction.value == null;
    }
    else {
      this.transaction.expenseAccountRef = results.data.companyAccounts.inventoryAdjustment;
      this.transaction.siteId = this.sites[0].id;
      this.edit = true;
    }
    this.init = false;
    if (!this.transactionId) {
      this.transaction.expenseAccountRef = results.data.companyAccounts.inventoryAdjustment;
      this.transaction.siteId = this.sites[0].id;
      this.edit = true;
    }
    await this.refresh();
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    changeBond() {
      if (!this.sites.some(site => site.id === this.transaction.siteId))
        this.transaction.siteId = this.sites[0].id;
      this.$nextTick(() => this.$refs.form.validate());
    },
    async refresh() {
      const results = await this.$apiCall(`query InventoryProductsReduceInventory($id:ID!,$asof:DateTime!) {
                                                product(id:$id) {
                                                  id,name,caseSize,
                                                  sites(asof:$asof) {siteId,lot,inBondBottles,dutyPaidBottles}
                                                }
                                              }`,
                                          {id:this.transaction ? this.transaction.productId : this.productId,
                                           asof:this.transaction.date});
      if (!results.error) {
        this.product = results.data.product;
        this.$nextTick(() => {this.$refs.form.validate()});
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      this.$refs.form.validate();
      if (!this.valid) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.transaction.value = this.calculateValue ? null : this.value;
      this.saving = true;
      const results = await this.$apiCall(`mutation InventoryProductsReduceInventory($input: ProductReduceInventoryInput!) {
                                            productReduceInventoryCreate(input:$input) {id}
                                          }`,
                                          {input:this.transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} reduce inventory transaction for ${this.product.name}`});
      }
		}
	}
}
</script>